import { z } from "zod";

/**
 * Buying sending domains and mailboxes through Smartlead's Smart Senders, for a product's cold email
 * connection (workers/src/email/orders.ts). The owner drafts an order (a vendor, domains, mailboxes, where
 * the domains forward, the registration contact), sees its price, and buys it with one press. Smartlead
 * charges the card on the Smartlead account; Flashadz never sees payment details.
 */

const domainName = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^(?=.{4,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/, "Use a domain name like getflashkut.com.");

export const MailboxDraftSchema = z.object({
  /** The full address, on its domain. */
  address: z.string().trim().toLowerCase().email(),
  firstName: z.string().trim().min(1).max(60),
  lastName: z.string().trim().min(1).max(60),
});

/** The registration contact the vendor needs for a domain (ICANN WHOIS). Smartlead's place-order user_details. */
export const ContactSchema = z.object({
  firstName: z.string().trim().min(1).max(60),
  lastName: z.string().trim().min(1).max(60),
  email: z.string().trim().email(),
  company: z.string().trim().min(1).max(120),
  country: z.string().trim().min(2).max(60),
  state: z.string().trim().min(1).max(60),
  city: z.string().trim().min(1).max(80),
  addressLineOne: z.string().trim().min(1).max(160),
  addressLineTwo: z.string().trim().max(160).default(""),
  postalCode: z.string().trim().min(2).max(20),
  /** With its plus, for example +1. */
  phoneCc: z.string().trim().regex(/^\+\d{1,4}$/, "Use a country code like +1."),
  phone: z.string().trim().regex(/^[\d\s-]{5,20}$/, "Use the phone number's digits."),
});
export type Contact = z.infer<typeof ContactSchema>;

/** Most mailboxes one domain carries in a Flashadz order: the cold-email skill keeps a domain's volume low. */
export const MAX_MAILBOXES_PER_DOMAIN = 3;

export const OrderDraftSchema = z
  .object({
    vendorId: z.number().int().positive(),
    domains: z
      .array(z.object({ domain: domainName, mailboxes: z.array(MailboxDraftSchema).min(1).max(MAX_MAILBOXES_PER_DOMAIN) }))
      .min(1)
      .max(10),
    /** Where visitors to the new domains are sent: the product's own website. */
    forwardingDomain: domainName,
    contact: ContactSchema,
  })
  .superRefine((o, ctx) => {
    const seen = new Set<string>();
    for (const [i, d] of o.domains.entries()) {
      if (seen.has(d.domain)) ctx.addIssue({ code: "custom", path: ["domains", i, "domain"], message: `${d.domain} is listed twice.` });
      seen.add(d.domain);
      for (const [j, m] of d.mailboxes.entries()) {
        if (!m.address.endsWith(`@${d.domain}`)) ctx.addIssue({ code: "custom", path: ["domains", i, "mailboxes", j, "address"], message: `${m.address} is not on ${d.domain}.` });
      }
    }
  });
export type OrderDraft = z.infer<typeof OrderDraftSchema>;

/** A vendor as Smartlead lists it (smart-senders/get-vendors), with its price in cents. */
export interface Vendor {
  id: number;
  name: string;
  /** What kind of mailboxes it makes, read from its name: Google, Outlook or SMTP. */
  provider: "Google" | "Outlook" | "SMTP" | "Other";
  domainCentsPerYear: number;
  mailboxCentsPerMonth: number;
  currency: string;
  /** Hours from order to delivery, as Smartlead reports them. */
  deliveryHours: number | null;
  /** The share of its orders delivered, as a percentage from 0 to 100 (99.32 means 99.32%). */
  deliveryRate: number | null;
  orders: number;
}

export interface DomainSearch {
  domain: string;
  available: boolean;
  suggestions: string[];
}

/**
 * An order's life: a draft the owner can change or drop; placing while the one press is sent; unsure after
 * an unclear answer, until Smartlead's purchased domains say; placed once Smartlead took it; ready once its
 * mailboxes are in the Smartlead account and set up; failed when Smartlead refused it cleanly.
 */
export type OrderState = "draft" | "placing" | "unsure" | "placed" | "ready" | "failed" | "cancelled";

export interface OrderQuote {
  currency: string;
  /** Charged once a year per domain, and once a month per mailbox. */
  domainsCentsPerYear: number;
  mailboxesCentsPerMonth: number;
}

export interface MailboxOrder {
  id: string;
  productId: string;
  vendorId: number;
  vendorName: string;
  /** What kind of mailboxes the vendor makes, and its usual hours to deliver, as when the order was drafted. */
  provider: Vendor["provider"];
  deliveryHours: number | null;
  domains: { domain: string; mailboxes: { address: string; firstName: string; lastName: string }[] }[];
  forwardingDomain: string;
  /** Who the domains are registered to. */
  contact: Contact;
  quote: OrderQuote;
  state: OrderState;
  /** What the owner needs to read: Smartlead's refusal, why it waits, which mailboxes arrived. */
  note: string | null;
  /** The Smartlead mailboxes this order made, once they are in the account. */
  mailboxIds: number[];
  placedAt: string | null;
  createdAt: string;
}

/** Mailboxes Flashadz sets up when an order arrives: the cold-email skill's start for a new mailbox, no warm-up. */
export const NEW_MAILBOX = { maxPerDay: 10, minutesBetween: 15 } as const;
